// src/buildInfo.js
//
// 15 Jul 2026 — added after repeatedly losing time this session to "is the
// fix actually deployed, or is the browser just showing a cached version?"
// Vercel exposes VERCEL_GIT_COMMIT_SHA as a build-time env var; vercel.json's
// buildCommand maps it to REACT_APP_BUILD_SHA, and stamps REACT_APP_BUILD_TIME
// from `date` at build time. Only REACT_APP_-prefixed vars reach the browser —
// a CRA restriction, not a Vercel one.
//
// BUILD_TIME is the time the bundle was BUILT, not the commit's timestamp.
// The original mapping read $VERCEL_GIT_COMMIT_TIMESTAMP, which is not a
// variable Vercel sets, so this silently stayed "local" on every deploy even
// after the SHA started resolving. Build time is the more useful of the two
// anyway: a redeploy of an unchanged commit is exactly the case you are trying
// to tell apart when you go looking at this.
//
// This turns "is this deployed yet?" from a multi-round Network-tab/git-log
// diagnostic into a 2-second console check: open DevTools, look for the
// "[Previu] build:" line logged on every page load, or run
// `window.__PREVIU_BUILD__` directly in the console.
//
// Local dev (`npm start`) has neither env var set — falls back to "dev"
// rather than showing an empty/misleading value.

export const BUILD_SHA  = process.env.REACT_APP_BUILD_SHA  || 'dev';
export const BUILD_TIME = process.env.REACT_APP_BUILD_TIME || 'local';

// Which pipeline produced this bundle. The SHA alone cannot tell you: a laptop
// `npx vercel --prod` stamps the same commit CI would, so answering "is this
// build from CI?" meant cross-referencing Actions runs by timestamp.
// "ci:<run id>" for a GitHub Actions deploy — paste it after
// github.com/Previu-Health/previu-frontend/actions/runs/ to open that exact run.
// "local" for a laptop deploy, which skips the design check, the warning gate
// and the bundle verification, and can carry uncommitted working-tree changes.
export const BUILD_SOURCE = process.env.REACT_APP_BUILD_SOURCE || 'local';

// The branch the bundle was built from. Two different sources, because neither
// covers both paths: CI sets PREVIU_BUILD_BRANCH itself (`vercel build` on a
// runner logs "Build not running on Vercel. System environment variables will
// not be available", so VERCEL_GIT_COMMIT_REF is empty there), while a laptop
// `npx vercel --prod` builds on Vercel's builders, where VERCEL_GIT_COMMIT_REF
// is populated from the git metadata the CLI attaches. "unknown" means neither
// was set — a build with no git context at all.
export const BUILD_BRANCH = process.env.REACT_APP_BUILD_BRANCH || 'unknown';

// Which environment this bundle is serving. Vercel calls the deployment behind
// the main alias "Production" and that name cannot be changed — but this
// deployment is our QA system, not a live production one, so the app says so.
//
//   qa       the previu-nav-app deployment — main, or a laptop deploy
//   uat      the previu-nav-uat deployment — a release/** branch
//   preview  a per-PR preview deployment
//   local    `npm start` — vercel.json's buildCommand never runs, so nothing
//            sets the variable at all
export const BUILD_ENV = process.env.REACT_APP_BUILD_ENV || 'local';

// Short SHA (7 chars, matching how git/GitHub display short commit hashes)
export const BUILD_SHA_SHORT = BUILD_SHA === 'dev' ? 'dev' : BUILD_SHA.slice(0, 7);

export function logBuildInfo() {
  const info = { env: BUILD_ENV, sha: BUILD_SHA_SHORT, fullSha: BUILD_SHA, builtAt: BUILD_TIME, branch: BUILD_BRANCH, source: BUILD_SOURCE };
  console.log('[Previu] build:', info);
  // Also attach to window so it's checkable without scrolling console history —
  // just run `window.__PREVIU_BUILD__` in DevTools at any point.
  window.__PREVIU_BUILD__ = info;
  return info;
}
